/* eslint-disable max-lines-per-function */

import React, { useState } from 'react';
import Big from 'big.js';
import moment from 'moment';
import { useMutation } from 'react-query';
import determinePurpose from 'utils/fxTrades/determinPurpose';
import t from 'utils/translationHelper';

import { CurrencyAccountDto } from '@alpha/currency-accounts-dtos';
import {
  DrawdownRequest, DrawdownValidationDto, MassDrawdownRequest, MassDrawdownValidationDto,
} from '@alpha/fx-dtos';
import { ManualPaymentRequest, PaymentDto, PaymentOnlineOrigin, PaymentStatus, PaymentValidationDto, PaymentVopMatchStatus } from '@alpha/payments-dtos';
import { ActionButton } from '@alpha/ui-lib/ui/button';
import { BaseDrawer } from '@alpha/ui-lib/ui/Drawer/APBaseDrawer';
import { StyledTabsWrapper } from '@alpha/ui-lib/ui/Tabs';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faAngleRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import useAlphaSnackbar from 'hooks/useAlphaSnackbar';
import useForm from 'hooks/useForm';
import useLog from 'hooks/useLog';
import instance from 'services/Axios/instance';
import DrawerBackdropLoader from '../../../Loaders/DrawerBackdropLoader/DrawerBackdropLoader';
import { FormikPaymentType } from '../../../Payments/CreatePaymentDrawer/formData';
import { FormikValueProps } from '../formData';
import Summary from '../Summary';
import { Section } from '..';

import validation, { initialValues } from './AddPayment/formData';
import PaymentIncompleteWarningModal from './AddPayment/PaymentIncompleteWarningModal';
import AddPayment from './AddPayment';
import VOPConfirmationModal from 'components/Payments/VOP/VOPConfirmationModal/VOPConfirmationModal';
import { PaymentsService } from 'services/Payments/payments.service';
import mapVopErrors from 'utils/payments/mapVopErrors';
import useDrawdownCOPCheck from '../../useDrawdownCOPCheck';
import PayeeConfirmationModal from 'domain/Beneficiaries/CreateBeneficiary/PayeeConfirmationModal/PayeeConfirmationModal';
import { ValidateBeneficiaryRequest, ValidateBeneficiaryResponse } from '@alpha/bene-dtos';
import { acceptCOPBeneficiary } from 'services/Beneficiaries/beneficiaries.service';

type ValidatePaymentParams = {
  data: FormikPaymentType,
  form: DrawdownRequest,
  buyCurrencyAccount?: CurrencyAccountDto,
  sellCurrencyAccount?: CurrencyAccountDto,
  validatedDrawdown?: DrawdownValidationDto,
  validatedDrawdowns?: MassDrawdownValidationDto,
  massDrawdownId?: string,
};

type Props = {
  handleSetSection: (_section: Section) => void;
  drawdownForm?: DrawdownRequest | MassDrawdownRequest;
  buyCurrencyAccount?: CurrencyAccountDto;
  sellCurrencyAccount?: CurrencyAccountDto;
  validatedDrawdown?: DrawdownValidationDto;
  setOpenPaymentClearWarning?: React.Dispatch<React.SetStateAction<boolean>>,
  setWarningModalConfirmationAction?: React.Dispatch<React.SetStateAction<'BACK_TO_DRAWDOWN' | 'CLOSE_DRAWER'>>,
  massDrawdownId?: string,
  validatedDrawdowns?: MassDrawdownValidationDto,
} & FormikValueProps;

const validatePayment = async ({
  data, form, buyCurrencyAccount, sellCurrencyAccount, validatedDrawdown, massDrawdownId,
}: ValidatePaymentParams) => {
  const response = await instance.post<ManualPaymentRequest, any>('/payments/validate', {
    beneficiaryId: data.beneficiary!.id,
    emailAddresses: data.emailAddresses,
    amount: +data.amount!,
    fundingCurrency: form.sellCurrencyCode,
    debitingCurrency: form.buyCurrencyCode,
    debitingAccountId: buyCurrencyAccount?.id || '',
    fundingAccountId: sellCurrencyAccount?.id || '',
    paymentDate: moment(data.date!).format('DD/MM/yyyy'),
    reference: data.reference || '',
    purpose: determinePurpose(data.purpose, data?.purposeType, data?.invoiceDate, data?.invoiceNumber),
    drawdownId: validatedDrawdown?.id,
    tradeId: validatedDrawdown?.tradeId,
    paymentOrigin: massDrawdownId ? 'MASS_DRAWDOWN' : PaymentOnlineOrigin.DRAWDOWN,
    massDrawdownId,
  });
  return response.data;
};

const Beneficiaries: React.FC<Props> = ({
  values,
  setFieldValue,
  drawdownForm,
  handleSetSection,
  buyCurrencyAccount,
  sellCurrencyAccount,
  validatedDrawdown,
  setOpenPaymentClearWarning,
  setWarningModalConfirmationAction,
  massDrawdownId,
  validatedDrawdowns,
}) => {
  const { performBeneficiaryCOPValidation, loading: loadingCOPCheck, beneficiaryNeedsCOPAcknowledgement, setBeneficiaryNeedsCOPAcknowledgement } = useDrawdownCOPCheck();
  const [tabIndex, setTabIndex] = useState<number>(0);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [paymentNeedsVOPConfirmation, setPaymentNeedsVOPConfirmation] = useState<PaymentDto | undefined>(undefined);
  const [loadingPayment, setLoadingPayment] = useState<boolean>(false);
  const sb = useAlphaSnackbar();
  const { logError, logEvent } = useLog();

  const loadPaymentData = async (id: string): Promise<PaymentDto | undefined> => {
    try {
      setLoadingPayment(true);
      const result = await PaymentsService.getSinglePayment(id);
      return result;
    } catch (error) {
      sb.trigger(error?.message || t('failed_to_load_payment'));
      logError({ action: 'Error loading payment', error });
      return undefined;
    } finally {
      setLoadingPayment(false);
    }
  };

  const deleteMutation = useMutation((_paymentIds: string[]) => instance.post('/payments/delete-drafts', { paymentIds: _paymentIds }), {  });

  const setFormData = (id: string, vopDetails?: PaymentDto['vopDetails'], vopRequired?: boolean, recommended?: boolean) => {
      const payment = { ...singlePaymentForm.values, id, vopDetails: vopDetails, vopRequired: vopRequired && !!vopDetails?.status, recommended: recommended };
      setFieldValue('payments', values.payments ? [...values.payments, payment] : [payment]);
      singlePaymentForm.resetForm();
      sb.trigger(t('payment_added'), 'success');

      logEvent({ action: 'Successfully added payment' });
      setTabIndex(1);
    };

  const handleVopErrors = (errors: string[]) => {
    const mappedErrors = mapVopErrors(errors);
    sb.trigger(mappedErrors, 'error');
    logError({ action: 'Error validating drawdown payment', detail: {errors} });
  };

  const validatePaymentMutations = useMutation(validatePayment, {
    onSuccess: async (data: PaymentValidationDto) => {
      if (data.status === PaymentStatus.INVALID) {
        sb.trigger(data.errors.length ? `${t('validation_error')} ${data.errors[0]}` : t('there_was_an_error_validating_your_payment'));
        logError({ action: 'Error validating drawdown payments', error: data.errors });
      } else if (data.status === PaymentStatus.VALIDATED) {

        // COP check for GBP drawdowns
        if (drawdownForm?.buyCurrencyCode === 'GBP' && beneficiary?.id) {
          const needsCOPAcknowledgement = await performBeneficiaryCOPValidation(beneficiary?.id, data.id);
          if (needsCOPAcknowledgement) {
            return;
          }
        }

        if(data.vopRequired
           && data.verificationOfPayeeResponse?.vopErrors
            && data.verificationOfPayeeResponse.vopErrors.length > 0) {
          handleVopErrors(data.verificationOfPayeeResponse.vopErrors);
          return;
        }

        // VOP happening
        if (data.vopRequired && data.verificationOfPayeeResponse && data.verificationOfPayeeResponse.status) {
          loadPaymentData(data.id).then((payment) => {
            setPaymentNeedsVOPConfirmation(payment);
          });
          return;
        }

       setFormData(data.id, data.verificationOfPayeeResponse, data.vopRequired);
      }
    },
  });

  const checkDrawdownsHaveEnoughBalance = (
    amount = 0,
    payments?: FormikPaymentType[],
    validatedDrawdownsData?: MassDrawdownValidationDto,
  ): boolean => {
    let total = new Big(0);

    if (payments) {
      for (let i = 0; i < payments.length; i += 1) {
        total = total.plus(payments[i].amount || 0);
      }
    }
    logEvent({
      action: 'Total Payments Amount',
      detail: {
        total: total.toString(),
        payments: payments !== undefined ? payments.length : 'No Payments',
      },
    });
    if (validatedDrawdownsData) {
      let balance = new Big(0);
      for (let i = 0; i < validatedDrawdownsData?.validatedDrawdowns?.length; i += 1) {
        balance = balance.plus(validatedDrawdownsData?.validatedDrawdowns?.[i].buyAmount || 0);
      }

      const enoughBalance = total.plus(amount).lte(balance);
      logEvent({
        action: 'Enough Balance',
        detail: {
          amount,
          total,
          balance,
          enoughBalance,
        },
      });
      return enoughBalance;
    }
    logEvent({
      action: 'Default checkDrawdownsHaveEnoughBalance',
    });
    return false;
  };
  const [disabled, setDisabled] = useState<boolean>(false);

  const checkDrawdownHasEnoughBalance = (
    amount = 0,
    payments?: FormikPaymentType[],
    validatedDrawdownData?: DrawdownValidationDto,
  ): boolean => {
    let total = new Big(0);
    if (payments) {
      for (let i = 0; i < payments.length; i += 1) {
        total = total.plus(payments[i].amount || 0);
      }
    }
    if (validatedDrawdownData) {
      const enoughBalance = total.plus(amount).lte(validatedDrawdownData?.buyAmount ?? 0);
      return enoughBalance;
    }
    return false;
  };
  const singlePaymentForm = useForm(initialValues, validation, (formValues: FormikPaymentType) => {
    if (massDrawdownId) {
      logEvent({
        action: 'Validate Mass Drawdown Payment',
        detail: {
          massDrawdownId,
          amount: formValues.amount,
          payments: values.payments,
          validatedDrawdowns,
        },
      });
      if (checkDrawdownsHaveEnoughBalance(
        formValues.amount,
        values.payments,
        validatedDrawdowns,
      )) {
        validatePaymentMutations.mutate({
          data: formValues,
          form: drawdownForm!,
          buyCurrencyAccount,
          sellCurrencyAccount,
          validatedDrawdown,
          massDrawdownId,
        });
      } else {
        sb.trigger(t('there_is_not_enough_drawdown_balance_for_this_payment'), 'error');
        logError({ action: 'Not enough drawdown balance for payment' });
      }
    } else if (checkDrawdownHasEnoughBalance(
      formValues.amount,
      values.payments,
      validatedDrawdown,
    )) {
      validatePaymentMutations.mutate({
        data: formValues,
        form: drawdownForm!,
        buyCurrencyAccount,
        sellCurrencyAccount,
        validatedDrawdown,
        massDrawdownId,
      });
    } else {
      sb.trigger(t('there_is_not_enough_drawdown_balance_for_this_payment'), 'error');
      logError({ action: 'Not enough drawdown balance for payment' });
    }
  }, true);

  const tabTitles: string[] = [t('add_payment'), `${t('summary')} (${values.payments?.length || 0})`];
  const {
    amount, emailAddresses, beneficiary, purpose, reference,
  } = singlePaymentForm.values;
  const isTouched = Boolean(amount || emailAddresses.length || beneficiary || purpose || reference);

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <div>
          <StyledTabsWrapper
            testId="payment-tabs-wrapper"
            tabTitles={tabTitles}
            tabIndex={tabIndex}
            handleChange={(selectedTabIndex: number) => setTabIndex(selectedTabIndex)}
          />
          <div style={{
            position: 'absolute', transform: 'translateY(-8px)', width: '100%', left: 0,
          }}
          >
            <BaseDrawer.LineBreak />
          </div>
          <div style={{ marginTop: '24px' }}>
            {
              tabIndex === 0 && (
                <AddPayment
                  setFieldValue={setFieldValue}
                  payments={values}
                  singlePaymentForm={singlePaymentForm}
                  drawdownForm={drawdownForm}
                  buyCurrencyAccount={buyCurrencyAccount}
                  setDisabled={setDisabled}
                />
              )
            }
            {
              tabIndex === 1 && (
                <Summary 
                values={values} 
                setFieldValue={setFieldValue} 
                addNewPayment={() => {
                  setTabIndex(0);
                  singlePaymentForm.resetForm();
                }}
                />
              )
            }
          </div>
        </div>
        <div style={{
          display: 'flex', flexDirection: 'row', padding: '92px 0 42px 0', justifyContent: 'space-between', gap: '8px',
        }}
        >
          <div style={{
            position: 'absolute', transform: 'translateY(-42px)', width: '100%', left: 0,
          }}
          >
            <BaseDrawer.LineBreak />
          </div>

          <ActionButton
            style={{
              background: '#F7F7F7', color: '#212529', width: '134px', whiteSpace: 'nowrap',
            }}
            onClick={() => {
              if (values.payments
                && values.payments?.length > 0
                && setOpenPaymentClearWarning
              ) {
                if (setWarningModalConfirmationAction) setWarningModalConfirmationAction('BACK_TO_DRAWDOWN');
                setOpenPaymentClearWarning(true);
              } else {
                handleSetSection('Drawdown');
              }
            }}
          >
            {' '}
            {t('back_to_drawdown')}
          </ActionButton>
          <ActionButton
            fullWidth
            disabled={(tabIndex === 1 && !values.payments?.length) || (tabIndex === 0 && !singlePaymentForm.isValid) || disabled}
            onClick={() => {
              if(tabIndex === 0) {
                singlePaymentForm.setFieldValue('singlePayment', false);
                singlePaymentForm.submitForm();
              }
              if(tabIndex === 1) {
                if (isTouched) {
                  setOpenModal(true);
                } else {
                  handleSetSection('Submit');
                }
              }
            }}
            size="medium"
          >
            {tabIndex === 0 ? t('save_payment') : t('continue_to_confirmation')}
            {' '}
            {tabIndex === 1 && <FontAwesomeIcon icon={faAngleRight as IconProp} />}
          </ActionButton>
        </div>
      </div>

      <PaymentIncompleteWarningModal handleClose={() => setOpenModal(false)} open={openModal} handleSubmit={() => handleSetSection('Submit')} />
      <VOPConfirmationModal
        open={!!paymentNeedsVOPConfirmation}
        handleClose={() => {
          // We have to remove the draft payment if the user do not acknowledge VOP, else it takes up the remaining drawdown balance
          if(paymentNeedsVOPConfirmation?.id) {
            deleteMutation.mutate([paymentNeedsVOPConfirmation?.id]); 
          }
          setPaymentNeedsVOPConfirmation(undefined);
        }}
        payment={paymentNeedsVOPConfirmation}
        handleActionButton={(data) => {
          if (data.paymentId) {
            setFormData(data.paymentId, data.vopDetails, data.vopRequired);
          }
          setPaymentNeedsVOPConfirmation(undefined);
        }}
      />
      <DrawerBackdropLoader
        display={validatePaymentMutations.isLoading || loadingPayment}
        text={t('validating_your_payment')}
        width={massDrawdownId ? '900px' : '600px'}
      />
      <PayeeConfirmationModal
        open={!!beneficiaryNeedsCOPAcknowledgement}
        handleClose={() => setBeneficiaryNeedsCOPAcknowledgement(undefined)}
        handleActionButton={(validationDetail?: object) => {
          // submit
          setFormData(validationDetail?.beneficiaryValidation?.paymentId);
          setBeneficiaryNeedsCOPAcknowledgement(undefined);
        }}
        formValues={beneficiaryNeedsCOPAcknowledgement ?? {
          name: '',
          accountNumber: '',
          nationalBankCode: '',
        } as unknown as ValidateBeneficiaryRequest} 
        validatedBene={{
          id: beneficiaryNeedsCOPAcknowledgement?.id,
          status: beneficiaryNeedsCOPAcknowledgement?.status,
          errors:beneficiaryNeedsCOPAcknowledgement?.errors,
          applyFinancialErrored: !!beneficiaryNeedsCOPAcknowledgement?.applyFinancialErrored,
          confirmationOfPayeeResponse: beneficiaryNeedsCOPAcknowledgement?.confirmationOfPayee,
          paymentId: beneficiaryNeedsCOPAcknowledgement?.paymentId,
        } as unknown as ValidateBeneficiaryResponse & { paymentId: string }}
        handleAcceptRecommendations={(beneficiaryId: string, paymentId: string) => {
          acceptCOPBeneficiary(beneficiaryId).then(() => {
            setBeneficiaryNeedsCOPAcknowledgement(undefined);
            setFormData(paymentId,undefined,undefined,true);
            sb.trigger(t('beneficiary_cop_recommendation_accepted'), 'success');
          }).catch((error) => {
            sb.trigger(error?.message || t('failed_to_accept_cop_recommendation'), 'error');
            logError({ action: 'Error accepting COP recommendation', error });
          });

        }}
      />
    </>
  );
};

export default Beneficiaries;
